import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import ukLocale from '@angular/common/locales/uk';
import { ApplicationConfig, LOCALE_ID, inject, isDevMode, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withRouterConfig } from '@angular/router';

import { AuthEffects, AuthInterceptor, AuthService, authReducer } from '@lib/auth';
import { PACKAGES_API_URL, insurancePackagesReducer } from '@lib/insurance-packages/data-access';
import { MEMBERS_API_URL } from '@lib/members/data-access';
import { protocolsReducer } from '@lib/protocols/data-access';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { initializeApp } from 'firebase/app';
import { getMessaging } from 'firebase/messaging';

import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';

import { API_URL, AUTH_API_URL, PROTECTED_API_URL } from '@lib/core/tokens/api.tokens';
import { FIREBASE_API_URL, FIREBASE_CONFIG, FIREBASE_MESSAGING } from '@lib/core/tokens/firebase.tokens';
import { provideTranslateModule } from '@lib/core/translate/translate.provider';

import { provideAdminChatConfig } from '../core/providers/provide-admin-chat-config.function';
import * as ClientCreateUiEffects from '../core/stores/client-create-ui/client-create-ui.effects';
import { clientCreateUiReducer } from '../core/stores/client-create-ui/client-create-ui.reducer';
import { environment } from '../environments/environment';
import { routes } from './app.routes';

registerLocaleData(ukLocale);

export const firebaseApp = initializeApp(environment.firebase);
export const messaging = getMessaging(firebaseApp);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(routes, withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })),
    provideStore({
      auth: authReducer,
      'insurance-packages': insurancePackagesReducer,
      clientCreateUi: clientCreateUiReducer,
      protocols: protocolsReducer,
    }),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      connectInZone: true, // If set to true, the connection is established within the Angular zone
    }),
    provideEffects([AuthEffects, ClientCreateUiEffects]),
    provideHttpClient(withXhr(), withInterceptors([AuthInterceptor])),
    provideTranslateModule(),
    provideAppInitializer(() => inject(AuthService).initializeTokens()),
    { provide: AUTH_API_URL, useValue: environment.authorizationApiUrl },
    { provide: API_URL, useValue: environment.protectedApiUrl },
    { provide: PROTECTED_API_URL, useValue: environment.protectedApiUrl },
    { provide: MEMBERS_API_URL, useValue: environment.protectedApiUrl },
    { provide: PACKAGES_API_URL, useValue: environment.packagesApiUrl },
    { provide: 'DICTIONARIES_API_URL', useValue: environment.dictionariesApiUrl },
    { provide: FIREBASE_API_URL, useValue: environment.protectedApiUrl },
    { provide: FIREBASE_CONFIG, useValue: environment.firebase },
    { provide: FIREBASE_MESSAGING, useValue: messaging },
    { provide: LOCALE_ID, useValue: 'uk' },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    {
      provide: MAT_ICON_DEFAULT_OPTIONS,
      useValue: { fontSet: 'material-symbols-outlined' },
    },
    provideNativeDateAdapter(),
    provideAdminChatConfig(),
  ],
};
